import React from 'react'
import { api } from '../api'
import { ProfessionKey } from '../professions'
import { detectTimeZone } from '../countryRules'

export type Role = 'patient' | 'specialist' | undefined

export type Appt = {
  notificationId?: string
  offerId?: string
  selectionId?: string
  specialty?: string
  chosenSlotISO: string
  feeCents?: number
  status?: string
  patientName?: string
  specialistName?: string
  profession?: ProfessionKey
  stack?: ProfessionKey
  seenAt?: string | null
  role?: Role
  review?: {
    specialist?: {
      canReview?: boolean
      submitted?: boolean
      rating?: number | null
      comment?: string | null
      createdAt?: string | null
      reviewerFirstName?: string | null
    }
    client?: {
      canReview?: boolean
      submitted?: boolean
      rating?: number | null
      comment?: string | null
      createdAt?: string | null
      reviewerFirstName?: string | null
    }
  }
}

export type RequestNotif = {
  notificationId?: string
  selectionId: string
  specialty?: string
  status: 'OPEN' | 'FILLED' | 'PENDING'
  whenISO?: string
  profession: ProfessionKey
  stack?: ProfessionKey
  seenAt?: string | null
  role?: Role
}

const SEEN_REVIEW_UPDATE_KEY = 'notif.review.update.v1'

export type ReviewPrompt = {
  id: string
  apptId: string
  selectionId?: string
  specialty?: string
  slotISO?: string
  role: Role
}

export type ReviewUpdate = {
  id: string
  apptId: string
  selectionId?: string
  specialty?: string
  slotISO?: string
  rating?: number | null
  comment?: string | null
  createdAt?: string | null
  reviewerFirstName?: string | null
  role: Role
  source: 'incoming' | 'outgoing'
}

const loadSeen = (key: string): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || '[]'))
  } catch {
    return new Set()
  }
}
const saveSeen = (key: string, values: Set<string>) => {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(values)))
  } catch {
    /* ignore */
  }
}

const apptBaseKey = (a: Appt) => {
  if (a.notificationId) return a.notificationId
  const core = a.offerId || a.selectionId || `${a.chosenSlotISO}`
  const profession = a.profession || 'doctor'
  return `${profession}:${core}`
}
export const apptId = (a: Appt) => {
  if (a.notificationId) return a.notificationId
  const base = apptBaseKey(a)
  const status = (a.status || 'unknown').toUpperCase()
  return `${base}::${status}`
}
export const requestId = (r: RequestNotif) =>
  r.notificationId || `${r.profession}:${r.selectionId}:${r.status}`

export const fmtDateTimeSydney = (iso?: string) => {
  if (!iso) return ''
  if (iso === 'REQUEST_BOOKING') return 'No time selected — contact the client to arrange'
  const timeZone = detectTimeZone()
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return new Date(iso).toLocaleString()
  }
}

const selectionKey = (a: Appt) => a.selectionId || a.offerId || a.chosenSlotISO
const deriveReviewEvents = (appts: Appt[], role: Role) => {
  const prompts: ReviewPrompt[] = []
  const updates: ReviewUpdate[] = []
  if (!role) return { prompts, updates }
  for (const appt of appts) {
    const sel = selectionKey(appt)
    const baseId = apptId(appt)
    if (!sel || !baseId) continue
    if (role === 'patient') {
      if (appt.review?.specialist?.canReview) {
        prompts.push({
          id: `prompt:patient:${sel}`,
          apptId: baseId,
          selectionId: appt.selectionId,
          specialty: appt.specialty,
          slotISO: appt.chosenSlotISO,
          role,
        })
      }
      if (appt.review?.client?.submitted) {
        const createdAt = appt.review.client.createdAt || appt.chosenSlotISO
        updates.push({
          id: `update:patient:${sel}:${createdAt}`,
          apptId: baseId,
          selectionId: appt.selectionId,
          specialty: appt.specialty,
          slotISO: appt.chosenSlotISO,
          rating: appt.review.client.rating ?? null,
          createdAt,
          role,
          source: 'incoming',
        })
      }
    } else if (role === 'specialist') {
      if (appt.review?.client?.canReview) {
        prompts.push({
          id: `prompt:specialist:${sel}`,
          apptId: baseId,
          selectionId: appt.selectionId,
          specialty: appt.specialty,
          slotISO: appt.chosenSlotISO,
          role,
        })
      }
      if (appt.review?.specialist?.submitted) {
        const createdAt = appt.review.specialist.createdAt || appt.chosenSlotISO
        updates.push({
          id: `update:specialist:${sel}:${createdAt}`,
          apptId: baseId,
          selectionId: appt.selectionId,
          specialty: appt.specialty,
          slotISO: appt.chosenSlotISO,
          rating: appt.review.specialist.rating ?? null,
          comment: appt.review.specialist.comment ?? null,
          reviewerFirstName: appt.review.specialist.reviewerFirstName ?? null,
          createdAt,
          role,
          source: 'incoming',
        })
      }
    }
  }
  return { prompts, updates }
}

type ContextValue = {
  navRole: Role
  canPatient: boolean
  canSpecialist: boolean
  appointments: Appt[]
  requests: RequestNotif[]
  unseenApptCount: number
  unseenReqCount: number
  unseenTotal: number
  pendingReviewCount: number
  unseenReviewUpdateCount: number
  reviewPrompts: ReviewPrompt[]
  reviewUpdates: ReviewUpdate[]
  isApptSeen: (id: string) => boolean
  isReqSeen: (id: string) => boolean
  markApptSeen: (id: string) => void
  markReqSeen: (id: string) => void
  markAllAppointmentsSeen: (ids?: string[]) => void
  markAllRequestsSeen: (ids?: string[]) => void
  markReviewUpdateSeen: (id: string) => void
  isReviewUpdateSeen: (id: string) => boolean
  refresh: () => void
}

const NotificationsContext = React.createContext<ContextValue | null>(null)

type ProviderProps = {
  children: React.ReactNode
  navRole: Role
  canPatient: boolean
  canSpecialist: boolean
  enabled: boolean
}

export function NotificationsProvider({
  children,
  navRole,
  canPatient,
  canSpecialist,
  enabled,
}: ProviderProps) {
  const [appointments, setAppointments] = React.useState<Appt[]>([])
  const [requests, setRequests] = React.useState<RequestNotif[]>([])
  const [seenAppt, setSeenAppt] = React.useState<Set<string>>(() => new Set())
  const [seenReq, setSeenReq] = React.useState<Set<string>>(() => new Set())
  const [reviewPrompts, setReviewPrompts] = React.useState<ReviewPrompt[]>([])
  const [reviewUpdates, setReviewUpdates] = React.useState<ReviewUpdate[]>([])
  const [seenReviewUpdates, setSeenReviewUpdates] = React.useState<Set<string>>(
    () => loadSeen(SEEN_REVIEW_UPDATE_KEY)
  )
  const markSeenOnServer = React.useCallback((ids: string[]) => {
    if (!ids.length) return
    api.notificationsSeen(ids).catch((err) => {
      console.warn('mark notifications seen failed', err)
    })
  }, [])

  const fetchNotifications = React.useCallback(async () => {
    if (!enabled || !navRole) {
      setAppointments([])
      setRequests([])
      setReviewPrompts([])
      setReviewUpdates([])
      setSeenAppt(new Set())
      setSeenReq(new Set())
      return
    }
    try {
      const res = await api.notificationsMine(navRole)
      const rawRequests = Array.isArray(res?.requests) ? res.requests : []
      const rawAppointments = Array.isArray(res?.appointments) ? res.appointments : []
      const parseTime = (iso?: string) => {
        const ts = Date.parse(iso || '')
        return Number.isFinite(ts) ? ts : 0
      }
      const nextRequests = rawRequests.map((r: RequestNotif) => ({
        ...r,
        profession: r.profession || r.stack || 'doctor',
      }))
      const nextAppointments = rawAppointments.map((a: Appt) => ({
        ...a,
        profession: a.profession || a.stack || 'doctor',
      }))
      nextRequests.sort((a, b) => parseTime(b.whenISO) - parseTime(a.whenISO))
      nextAppointments.sort((a, b) => parseTime(a.chosenSlotISO) - parseTime(b.chosenSlotISO))
      setRequests(nextRequests)
      setAppointments(nextAppointments)
      setSeenReq(new Set(nextRequests.filter(r => r.seenAt).map(requestId)))
      setSeenAppt(new Set(nextAppointments.filter(a => a.seenAt).map(apptId)))
      const { prompts, updates } = deriveReviewEvents(nextAppointments, navRole)
      setReviewPrompts(prompts)
      setReviewUpdates(updates)
    } catch {
      setAppointments([])
      setRequests([])
      setReviewPrompts([])
      setReviewUpdates([])
      setSeenAppt(new Set())
      setSeenReq(new Set())
    }
  }, [enabled, navRole])

  React.useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])
  const refresh = React.useCallback(() => {
    fetchNotifications()
  }, [fetchNotifications])

  const markAppointments = React.useCallback((ids: string[]) => {
    if (!ids.length) return
    const idSet = new Set(ids)
    setSeenAppt(prev => {
      let changed = false
      const next = new Set(prev)
      idSet.forEach((id) => {
        if (!next.has(id)) {
          next.add(id)
          changed = true
        }
      })
      return changed ? next : prev
    })
    setAppointments(prev =>
      prev.map((appt) => (idSet.has(apptId(appt)) ? { ...appt, seenAt: appt.seenAt || new Date().toISOString() } : appt))
    )
    markSeenOnServer(ids)
  }, [markSeenOnServer])

  const markRequests = React.useCallback((ids: string[]) => {
    if (!ids.length) return
    const idSet = new Set(ids)
    setSeenReq(prev => {
      let changed = false
      const next = new Set(prev)
      idSet.forEach((id) => {
        if (!next.has(id)) {
          next.add(id)
          changed = true
        }
      })
      return changed ? next : prev
    })
    setRequests(prev =>
      prev.map((req) => (idSet.has(requestId(req)) ? { ...req, seenAt: req.seenAt || new Date().toISOString() } : req))
    )
    markSeenOnServer(ids)
  }, [markSeenOnServer])

  const markReviewUpdateSeen = React.useCallback((id: string) => {
    if (!id) return
    setSeenReviewUpdates(prev => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      saveSeen(SEEN_REVIEW_UPDATE_KEY, next)
      return next
    })
  }, [])

  const markAllReviewUpdatesSeen = React.useCallback(
    (ids?: string[]) => {
      const target = ids && ids.length ? ids : reviewUpdates.map(u => u.id)
      if (!target.length) return
      setSeenReviewUpdates(prev => {
        let changed = false
        const next = new Set(prev)
        for (const id of target) {
          if (!next.has(id)) {
            next.add(id)
            changed = true
          }
        }
        if (changed) saveSeen(SEEN_REVIEW_UPDATE_KEY, next)
        return changed ? next : prev
      })
    },
    [reviewUpdates]
  )

  const markApptSeen = React.useCallback((id: string) => markAppointments([id]), [markAppointments])
  const markReqSeen = React.useCallback((id: string) => markRequests([id]), [markRequests])
  const markAllAppointmentsSeen = React.useCallback(
    (ids?: string[]) => {
      const target = ids && ids.length ? ids : appointments.map(apptId)
      markAppointments(target)
    },
    [appointments, markAppointments]
  )
  const markAllRequestsSeen = React.useCallback(
    (ids?: string[]) => {
      const target = ids && ids.length ? ids : requests.map(requestId)
      markRequests(target)
    },
    [requests, markRequests]
  )

  const unseenApptCount = appointments.reduce((acc, a) => acc + (seenAppt.has(apptId(a)) ? 0 : 1), 0)
  const unseenReqCount = requests.reduce((acc, r) => acc + (seenReq.has(requestId(r)) ? 0 : 1), 0)
  const unseenTotal = unseenApptCount + unseenReqCount
  const pendingReviewCount = reviewPrompts.length
  const unseenReviewUpdateCount = reviewUpdates.reduce(
    (acc, r) => acc + (seenReviewUpdates.has(r.id) ? 0 : 1),
    0
  )

  const isApptSeen = React.useCallback((id: string) => seenAppt.has(id), [seenAppt])
  const isReqSeen = React.useCallback((id: string) => seenReq.has(id), [seenReq])

  const isReviewUpdateSeen = React.useCallback(
    (id: string) => seenReviewUpdates.has(id),
    [seenReviewUpdates]
  )

  const value = React.useMemo<ContextValue>(() => ({
    navRole,
    canPatient,
    canSpecialist,
    appointments,
    requests,
    unseenApptCount,
    unseenReqCount,
    unseenTotal,
    pendingReviewCount,
    unseenReviewUpdateCount,
    reviewPrompts,
    reviewUpdates,
    isApptSeen,
    isReqSeen,
    isReviewUpdateSeen,
    markApptSeen,
    markReqSeen,
    markAllAppointmentsSeen,
    markAllRequestsSeen,
    markReviewUpdateSeen,
    refresh,
  }), [
    appointments,
    requests,
    unseenApptCount,
    unseenReqCount,
    unseenTotal,
    pendingReviewCount,
    unseenReviewUpdateCount,
    reviewPrompts,
    reviewUpdates,
    isReviewUpdateSeen,
    navRole,
    canPatient,
    canSpecialist,
    isApptSeen,
    isReqSeen,
    markApptSeen,
    markReqSeen,
    markAllAppointmentsSeen,
    markAllRequestsSeen,
    markReviewUpdateSeen,
    refresh,
  ])

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  const ctx = React.useContext(NotificationsContext)
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationsProvider')
  }
  return ctx
}
